/**
 * webrtcService.js — WebRTC eş bağlantı yöneticisi
 * ---------------------------------------------------------------------------
 * Sesin gerçekte aktığı yer burasıdır. Sunucu yalnızca sinyalleşmeyi taşır;
 * ses trafiği telefonlar arasında DOĞRUDAN gider (P2P). P2P kurulamazsa
 * TURN devreye girer — bu karar tarayıcının ICE motoruna aittir.
 *
 * ═══════════════ TEKLİF ÇAKIŞMASI (GLARE) NASIL ÖNLENİYOR ═══════════════
 * İki taraf aynı anda teklif gönderirse WebRTC bağlantısı bozulur. Bunu
 * karmaşık "kibar/kaba eş" (perfect negotiation) mantığıyla değil, basit ve
 * kesin bir kuralla çözüyoruz:
 *
 *     HER EŞ ÇİFTİNDE UID SIRASI KÜÇÜK OLAN TARAF TEKLİFİ GÖNDERİR.
 *
 * Yeni biri katıldığında hem `call:participant-joined` olayı hem de `ready`
 * yanıtındaki katılımcı listesi bağlantıyı tetikleyebilir. İki uç da aynı UID
 * karşılaştırmasını yaptığı için bunlardan yalnızca biri offer üretir. Böylece
 * olayların geliş sırası değişse de bağlantı için teklif yönü tektir.
 *
 * ═══════════════ SUNUCU YÜKÜ ═══════════════
 * Bağlantı kurulduktan sonra bu modül sunucuya HİÇ istek atmaz. Yalnızca
 * kurulum sırasında teklif/cevap ve ICE adayları gider (bir bağlantı için
 * tipik olarak 10-20 küçük istek), sonrasında sıfır.
 * ---------------------------------------------------------------------------
 */

import { sinyalGonder } from "./callService";

/** Aynı anda tek bir arama oturumu olur. */
let oturum = null;
let oturumSirasi = 0;
const oynatmaBekleyenleri = new Set();

function sinyalKimligi(kind) {
  const rastgele = Math.random().toString(36).slice(2, 10);
  return `${kind}-${Date.now().toString(36)}-${rastgele}`;
}

/**
 * Eş rolü bütün cihazlarda aynı sonucu vermelidir.
 *
 * `localeCompare()` cihazın dil/kolasyon ayarını kullanır. Türkçe iPhone ile
 * İngilizce Android aynı karma harf/rakam içeren Firebase UID çiftini farklı
 * sıralayabilir. Bunun sonucu iki tarafın da teklifçi olması (glare) veya iki
 * tarafın da beklemesi ve hiç SDP üretmemesidir. Unicode kod birimi sırası
 * locale'den bağımsızdır ve iki uçta kesin olarak aynıdır.
 */
function uidKarsilastir(a, b) {
  const sol = String(a || "");
  const sag = String(b || "");
  if (sol === sag) return 0;
  return sol < sag ? -1 : 1;
}

/**
 * Her eş çifti için yalnızca bir taraf teklif üretir. Karar callId'den veya
 * katılma sırasından bağımsızdır; iki cihaz da aynı UID karşılaştırmasından aynı
 * sonucu çıkarır. Böylece `ready` yanıtı ile `participant-joined` olayı aynı
 * anda işlense bile çift offer (glare) oluşmaz.
 */
function teklifSahibiMi(uid) {
  if (!oturum) return false;
  return uidKarsilastir(oturum.benimUid, uid) < 0;
}

async function guvenilirSinyalGonder(callId, payload, tekrar = 2) {
  const tam = { ...payload, signalId: payload.signalId || sinyalKimligi(payload.kind) };
  let sonHata = null;
  for (let i = 0; i <= tekrar; i += 1) {
    try {
      const sonuc = await sinyalGonder(callId, tam);
      // Backend alıcı çevrimdışıysa SDP/cevabı kısa ömürlü kuyruğa alır.
      if (sonuc?.delivered > 0 || sonuc?.queued === true || payload.kind === "ice") return sonuc;
      sonHata = new Error("signal_not_delivered");
    } catch (e) {
      sonHata = e;
    }
    if (i < tekrar) await new Promise((r) => setTimeout(r, 350 * (i + 1)));
  }
  throw sonHata || new Error("signal_failed");
}

/** Dışarıya durum bildirmek için (UI güncellemesi). */
let durumDinleyici = null;

export function durumDinleyiciAta(fn) {
  durumDinleyici = typeof fn === "function" ? fn : null;
}

function durumBildir() {
  if (!durumDinleyici || !oturum) return;

  const baglantiDurumlari = {};
  const medyaDurumlari = {};
  oturum.peers.forEach((p, uid) => {
    baglantiDurumlari[uid] = p.pc ? p.pc.connectionState : "new";
    medyaDurumlari[uid] = !!p.medyaAkisVar;
  });

  durumDinleyici({
    callId: oturum.callId,
    roomId: oturum.roomId,
    baglantilar: Array.from(oturum.peers.keys()),
    baglantiDurumlari,
    bagliSayisi: Array.from(oturum.peers.values()).filter(
      (p) => p.pc && p.pc.connectionState === "connected"
    ).length,
    tasimaHazirSayisi: Array.from(oturum.peers.values()).filter(
      (p) => p.pc && (
        p.pc.connectionState === "connected"
        || p.pc.iceConnectionState === "connected"
        || p.pc.iceConnectionState === "completed"
      )
    ).length,
    medyaHazirSayisi: Array.from(oturum.peers.values()).filter(
      (p) => p.pc && p.pc.connectionState === "connected" && p.medyaAkisVar
    ).length,
    medyaDurumlari,
    susturuldu: oturum.susturuldu,
    sesSeviyeleri: { ...oturum.sesSeviyeleri },
    kaliteler: { ...oturum.kaliteler },
  });
}

function sesElemaniniOynat(audioEl) {
  if (!audioEl) return;
  if (!audioEl.srcObject) {
    oynatmaBekleyenleri.add(audioEl);
    return;
  }
  /* Aynı audio elementi için üst üste play() çağrıları iOS WKWebView'de
     önceki Promise'i AbortError ile kesebiliyor. Tek oynatma denemesi yürüt;
     CallKit audioActive olduktan veya medya hazırlandıktan sonra sınırlı,
     tamamen yerel bir tekrar yap. */
  if (audioEl.dataset.playPending === "1") return;
  try {
    audioEl.muted = false;
    audioEl.volume = 1;
    audioEl.dataset.playPending = "1";
    const p = audioEl.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        audioEl.dataset.playPending = "0";
        audioEl.dataset.playRetry = "0";
        oynatmaBekleyenleri.delete(audioEl);
      }).catch((error) => {
        audioEl.dataset.playPending = "0";
        oynatmaBekleyenleri.add(audioEl);
        if (audioEl.dataset.playErrorLogged !== "1") {
          audioEl.dataset.playErrorLogged = "1";
          console.warn("[AileCall:audio] Uzak ses oynatılamadı", error?.name || "play_failed");
        }
        const deneme = Number(audioEl.dataset.playRetry || 0);
        if ((error?.name === "AbortError" || error?.name === "NotAllowedError")
            && deneme < 6 && audioEl.isConnected && audioEl.srcObject) {
          audioEl.dataset.playRetry = String(deneme + 1);
          setTimeout(() => sesElemaniniOynat(audioEl), 180 * (deneme + 1));
        }
      });
    } else {
      audioEl.dataset.playPending = "0";
      oynatmaBekleyenleri.delete(audioEl);
    }
  } catch (_) {
    audioEl.dataset.playPending = "0";
    oynatmaBekleyenleri.add(audioEl);
  }
}

function bekleyenSesleriOynat() {
  if (!oynatmaBekleyenleri.size) return;
  Array.from(oynatmaBekleyenleri).forEach((audioEl) => {
    if (!audioEl || !audioEl.isConnected || !audioEl.srcObject) {
      oynatmaBekleyenleri.delete(audioEl);
      return;
    }
    sesElemaniniOynat(audioEl);
  });
}

if (typeof window !== "undefined") {
  ["touchstart", "pointerdown", "click", "keydown"].forEach((ad) => {
    window.addEventListener(ad, bekleyenSesleriOynat, { passive: true, capture: true });
  });
}

export function sesCikisiniCanlandir() {
  if (!oturum) return;
  oturum.peers.forEach((p) => {
    if (p.audioEl && p.audioEl.srcObject) sesElemaniniOynat(p.audioEl);
  });
  bekleyenSesleriOynat();
}

/* ══════════════════════════════════════════════════════════════════════════
   OTURUM YAŞAM DÖNGÜSÜ
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Yeni bir arama oturumu başlatır (henüz eş bağlantısı kurmaz).
 *
 * @param {object} p
 * @param {string} p.callId
 * @param {string} p.roomId
 * @param {string} p.benimUid
 * @param {MediaStream} p.yerelAkis   Mikrofon akışı
 * @param {object} p.rtcConfig        iceServers içeren yapılandırma
 */
export function oturumBaslat({ callId, roomId, benimUid, yerelAkis, rtcConfig }) {
  oturumKapat(); // Önceki oturum varsa temizle

  oturum = {
    oturumNo: ++oturumSirasi,
    callId: String(callId),
    roomId: String(roomId),
    benimUid: String(benimUid),
    yerelAkis,
    rtcConfig,
    peers: new Map(),   // uid -> { pc, audioEl, bekleyenIce: [], uzakAyarlandi: bool }
    susturuldu: false,
    sesSeviyeleri: {},  // uid -> 0..1  (kim konuşuyor)
    kaliteler: {},      // uid -> "iyi" | "orta" | "zayif"
    seviyeTimer: null,
    islenenSinyaller: new Set(),
  };
  seviyeOlcumuBaslat();
  durumBildir();
  return oturum;
}

export function oturumVarMi() {
  return !!oturum;
}

export function aktifCallId() {
  return oturum ? oturum.callId : null;
}

/** Tüm bağlantıları kapatır, mikrofonu bırakır, ses elemanlarını siler. */
export function oturumKapat() {
  if (!oturum) return;

  if (oturum.seviyeTimer) {
    clearTimeout(oturum.seviyeTimer);
    oturum.seviyeTimer = null;
  }

  oturum.peers.forEach((p) => {
    if (p.toparlanmaTimer) clearTimeout(p.toparlanmaTimer);
    if (p.baglantiTimer) clearTimeout(p.baglantiTimer);
    try { if (p.pc) p.pc.close(); } catch (_) { /* yoksay */ }
    try {
      if (p.audioEl) {
        p.audioEl.srcObject = null;
        p.audioEl.remove();
        oynatmaBekleyenleri.delete(p.audioEl);
      }
    } catch (_) { /* yoksay */ }
  });
  oturum.peers.clear();

  /* Mikrofonu KESİN olarak bırak.
     ÖNEMLİ: Parçalar durdurulmazsa iOS'ta ses oturumu açık kalıyor ve hemen
     ardından başlatılan İKİNCİ aramada mikrofon sessiz kalıyor ("karşı taraf
     açıyor ama ses gitmiyor" belirtisi). Önce enabled=false, sonra stop() —
     bu sıra WKWebView'de daha güvenilir sonuç veriyor. */
  try {
    if (oturum.yerelAkis) {
      oturum.yerelAkis.getTracks().forEach((t) => {
        try { t.enabled = false; } catch (_) { /* yoksay */ }
        try { t.stop(); } catch (_) { /* yoksay */ }
      });
    }
  } catch (_) { /* yoksay */ }

  oturum = null;
  if (durumDinleyici) durumDinleyici(null);
}

/* ══════════════════════════════════════════════════════════════════════════
   KİM KONUŞUYOR — SES SEVİYESİ ÖLÇÜMÜ

   `RTCPeerConnection.getStats()` içindeki `inbound-rtp.audioLevel` alanı
   okunur. AudioContext + AnalyserNode yaklaşımı yerine bu tercih edildi:
   WKWebView'de AudioContext, ses oturumu (playAndRecord) ile çakışıp uzak
   sesi kısabiliyor. getStats ise tamamen pasiftir ve ses yolunu etkilemez.

   SUNUCU YÜKÜ: Tamamen yereldir, sunucuya HİÇBİR istek gitmez.
   Ölçüm 500 ms'de bir yapılır — pil tüketimi ihmal edilebilir.
   ══════════════════════════════════════════════════════════════════════════ */

const KONUSMA_ESIGI = 0.02;   // Bu değerin üstü "konuşuyor" sayılır

function olcumPeriyodu() {
  // Mesh'te eş sayısı arttıkça ölçüm sıklığını düşür — 6 kişilik aramada
  // saniyede 10+ getStats çağrısı pili gereksiz tüketir.
  const n = oturum ? oturum.peers.size : 0;
  return n > 2 ? 800 : 500;
}

/** getStats sonucundan kalite özeti çıkarır. */
function kaliteyiSiniflandir(rtt, kayipYuzde) {
  if (rtt == null && kayipYuzde == null) return null;
  const r = rtt == null ? 0 : rtt;
  const k = kayipYuzde == null ? 0 : kayipYuzde;
  if (r > 0.5 || k > 8) return "zayif";
  if (r > 0.25 || k > 3) return "orta";
  return "iyi";
}

function seviyeOlcumuBaslat() {
  if (!oturum || oturum.seviyeTimer) return;

  const tik = async () => {
    if (!oturum) return;
    const buOturum = oturum;
    let degisti = false;
    let benimSeviye = 0;
    let ilkEs = true;

    for (const [uid, kayit] of buOturum.peers.entries()) {
      if (oturum !== buOturum) return;
      if (!kayit.pc || kayit.pc.connectionState !== "connected") continue;

      let seviye = 0;
      let rtt = null;
      let kayipYuzde = null;
      const oncekiMedyaDurumu = !!kayit.medyaAkisVar;

      /* TEK getStats ÇAĞRISI ile hem ses seviyesi hem kalite okunur.
         Önceki sürümde yerel seviye için ayrı bir çağrı daha yapılıyordu;
         6 kişilik aramada bu gereksiz yere iki katı maliyet demekti. */
      try {
        const stats = await kayit.pc.getStats();
        if (oturum !== buOturum) return;
        stats.forEach((r) => {
          const medyaTuru = r.kind || r.mediaType;
          if (r.type === "inbound-rtp" && medyaTuru === "audio") {
            if (typeof r.audioLevel === "number") seviye = Math.max(seviye, r.audioLevel);
            const alinan = Number(r.packetsReceived || 0);
            const kayip = Number(r.packetsLost || 0);
            const bayt = Number(r.bytesReceived || 0);
            if (alinan > 0 && bayt > 0) kayit.medyaAkisVar = true;
            if (alinan + kayip > 0) kayipYuzde = (kayip / (alinan + kayip)) * 100;
          }
          if (r.type === "candidate-pair" && r.state === "succeeded"
              && typeof r.currentRoundTripTime === "number") {
            rtt = r.currentRoundTripTime;
          }
          // Yerel mikrofon seviyesi — yalnızca ilk eşten okunur (kaynak aynı).
          if (ilkEs && r.type === "media-source" && medyaTuru === "audio"
              && typeof r.audioLevel === "number") {
            benimSeviye = Math.max(benimSeviye, r.audioLevel);
          }
        });
      } catch (_) { /* tarayıcı desteklemiyor olabilir — sessiz geç */ }
      ilkEs = false;

      if (oncekiMedyaDurumu !== !!kayit.medyaAkisVar) {
        degisti = true;
        sesElemaniniOynat(kayit.audioEl);
      }

      const yeniSeviye = seviye > KONUSMA_ESIGI ? Math.min(1, seviye * 4) : 0;
      if (Math.abs((buOturum.sesSeviyeleri[uid] || 0) - yeniSeviye) > 0.08) {
        buOturum.sesSeviyeleri[uid] = yeniSeviye;
        degisti = true;
      }

      const yeniKalite = kaliteyiSiniflandir(rtt, kayipYuzde);
      if (yeniKalite && buOturum.kaliteler[uid] !== yeniKalite) {
        buOturum.kaliteler[uid] = yeniKalite;
        degisti = true;
      }
    }

    // Susturulmuşken kendi seviyemiz her zaman 0 görünmeli.
    if (oturum !== buOturum) return;
    const benimYeni = (!buOturum.susturuldu && benimSeviye > KONUSMA_ESIGI)
      ? Math.min(1, benimSeviye * 4) : 0;
    if (Math.abs((buOturum.sesSeviyeleri[buOturum.benimUid] || 0) - benimYeni) > 0.08) {
      buOturum.sesSeviyeleri[buOturum.benimUid] = benimYeni;
      degisti = true;
    }

    if (degisti) durumBildir();

    // Periyot eş sayısına göre değiştiği için her turda yeniden kurulur.
    if (oturum === buOturum) {
      buOturum.seviyeTimer = setTimeout(tik, olcumPeriyodu());
      if (typeof buOturum.seviyeTimer.unref === "function") buOturum.seviyeTimer.unref();
    }
  };

  oturum.seviyeTimer = setTimeout(tik, olcumPeriyodu());
  if (typeof oturum.seviyeTimer.unref === "function") oturum.seviyeTimer.unref();
}

/* ══════════════════════════════════════════════════════════════════════════
   EŞ BAĞLANTISI
   ══════════════════════════════════════════════════════════════════════════ */

function esOlustur(uid) {
  if (!oturum) return null;
  const mevcut = oturum.peers.get(uid);
  if (mevcut) return mevcut;

  const pc = new RTCPeerConnection(oturum.rtcConfig);

  // Yerel mikrofonu bağlantıya ekle.
  try {
    oturum.yerelAkis.getTracks().forEach((track) => pc.addTrack(track, oturum.yerelAkis));
  } catch (_) { /* yoksay */ }

  // Uzak ses geldiğinde çalmaya başla.
  const audioEl = document.createElement("audio");
  audioEl.autoplay = true;
  audioEl.playsInline = true;
  audioEl.muted = false;
  audioEl.volume = 1;
  audioEl.setAttribute("playsinline", "true");
  audioEl.setAttribute("webkit-playsinline", "true");
  // iOS: sessiz mod anahtarından etkilenmemesi için native taraf ses
  // oturumunu 'playAndRecord' moduna alır (FAZ 5).
  audioEl.setAttribute("data-call-peer", uid);
  audioEl.preload = "auto";
  audioEl.controls = false;
  /* Mobil WebView'ler display:none olan canlı MediaStream elemanını uyutabilir.
     Eleman görünmez ve erişilemez kalır ama render ağacında 1px olarak yaşar. */
  Object.assign(audioEl.style, {
    position: "fixed",
    width: "1px",
    height: "1px",
    left: "-10000px",
    top: "0",
    opacity: "0",
    pointerEvents: "none",
  });
  document.body.appendChild(audioEl);

  pc.ontrack = (e) => {
    const kayit2 = oturum && oturum.peers.get(uid);
    if (!kayit2) return;

    /* Mobil WebView'lerde (özellikle iOS WKWebView ve bazı Android System
       WebView sürümlerinde) ontrack her zaman streams[0] ile gelmez. Önceki
       kod bu durumda uzak sesi tamamen yok sayıyordu: bağlantı "connected"
       görünür, süre ilerler, mikrofon seviyesi algılanır ama ahize/hoparlörden
       hiçbir ses çıkmaz. Stream yoksa track üzerinden kendi MediaStream'imizi
       kuruyoruz. */
    let uzakAkis = e.streams && e.streams[0] ? e.streams[0] : kayit2.uzakAkis;
    if (!uzakAkis) {
      uzakAkis = new MediaStream();
      kayit2.uzakAkis = uzakAkis;
    }
    if (e.track && !uzakAkis.getTracks().some((t) => t.id === e.track.id)) {
      try { uzakAkis.addTrack(e.track); } catch (_) { /* yoksay */ }
    }
    if (audioEl.srcObject !== uzakAkis) audioEl.srcObject = uzakAkis;
    audioEl.dataset.playErrorLogged = "0";
    sesElemaniniOynat(audioEl);
    if (e.track) e.track.onunmute = () => sesElemaniniOynat(audioEl);
  };

  audioEl.oncanplay = () => sesElemaniniOynat(audioEl);

  // ICE adaylarını karşı tarafa yolla (trickle ICE — beklemeden gönder).
  pc.onicecandidate = (e) => {
    if (!e.candidate || !oturum) return;
    guvenilirSinyalGonder(oturum.callId, {
      to: uid,
      kind: "ice",
      data: e.candidate.toJSON ? e.candidate.toJSON() : e.candidate,
    }, 0).catch(() => { /* ICE adayları çokludur; tek aday kaybı tolere edilir */ });
  };

  /* ══════════════════════════════════════════════════════════════════════
     FAZ 3 — AĞ DEĞİŞİMİNDE OTOMATİK TOPARLANMA

     ÖNCEKİ HATA: Bağlantı koptuğunda yalnızca `pc.restartIce()` çağrılıyordu.
     Ama restartIce() TEK BAŞINA HİÇBİR ŞEY YAPMAZ — sadece `negotiationneeded`
     olayını tetikler. O olayı dinleyip YENİ BİR TEKLİF göndermezsek bağlantı
     sessizce ölü kalır. WiFi'dan mobil veriye geçince arama kopuyordu ve bir
     daha toparlanmıyordu.

     ÇÖZÜM: `onnegotiationneeded` dinleniyor ve yeniden teklif gönderiliyor.

     GLARE KORUMASI: Yeniden pazarlığı yalnızca TEKLİF SAHİBİ taraf yapar
     (`tekliSahibi`). Karşı taraf yalnızca cevap üretir — böylece iki taraf
     aynı anda teklif göndermez. Bu, ilk kurulumdaki kuralla aynı mantıktır.

     "disconnected" GEÇİCİ OLABİLİR: Hücresel ağlarda birkaç saniyeliğine
     "disconnected" görülüp kendiliğinden düzelebilir. Bu yüzden hemen
     müdahale etmeyip 4 saniye bekliyoruz; hâlâ düzelmediyse ICE restart.
     "failed" ise kesin bozulmadır, anında müdahale edilir. */
  pc.onconnectionstatechange = () => {
    durumBildir();
    const kayit2 = oturum && oturum.peers.get(uid);
    if (!kayit2) return;

    if (pc.connectionState === "connected") {
      sesElemaniniOynat(audioEl);
      if (kayit2.toparlanmaTimer) {
        clearTimeout(kayit2.toparlanmaTimer);
        kayit2.toparlanmaTimer = null;
      }
      kayit2.toparlanmaDenemesi = 0;
      if (kayit2.baglantiTimer) {
        clearTimeout(kayit2.baglantiTimer);
        kayit2.baglantiTimer = null;
      }
      return;
    }

    if (pc.connectionState === "failed") {
      iceYenidenBaslat(uid);
      return;
    }

    if (pc.connectionState === "disconnected" && !kayit2.toparlanmaTimer) {
      kayit2.toparlanmaTimer = setTimeout(() => {
        kayit2.toparlanmaTimer = null;
        if (pc.connectionState !== "connected") iceYenidenBaslat(uid);
      }, 4000);
    }
  };

  pc.oniceconnectionstatechange = () => {
    durumBildir();
    if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
      sesElemaniniOynat(audioEl);
    }
  };

  /* Yeniden pazarlık gerektiğinde (ICE restart dahil) yeni teklif gönder.
     Yalnızca teklif sahibi taraf gönderir — glare önleme. */
  pc.onnegotiationneeded = async () => {
    if (!oturum) return;
    const kayit2 = oturum.peers.get(uid);
    if (!kayit2 || !kayit2.tekliSahibi) return;
    if (pc.signalingState !== "stable") return;
    try { await teklifOlusturVeGonder(uid, kayit2, false); }
    catch (_) { /* kontrollü toparlanma tekrar deneyecek */ }
  };

  const kayit = {
    pc,
    audioEl,
    bekleyenIce: [],
    uzakAyarlandi: false,
    tekliSahibi: teklifSahibiMi(uid),
    makingOffer: false,
    ignoreOffer: false,
    polite: uidKarsilastir(oturum.benimUid, uid) > 0,
    toparlanmaTimer: null,
    toparlanmaDenemesi: 0,
    baglantiTimer: null,
    medyaAkisVar: false,
    kalite: null,            // { rtt, kayipYuzde, seviye }
    uzakAkis: null,
  };
  oturum.peers.set(uid, kayit);
  durumBildir();
  return kayit;
}

async function teklifOlusturVeGonder(uid, kayit, iceRestart = false) {
  if (!oturum || !kayit?.pc || kayit.makingOffer) return;
  if (kayit.pc.signalingState !== "stable") return;
  const buOturum = oturum;
  kayit.makingOffer = true;
  try {
    const offer = await kayit.pc.createOffer({
      offerToReceiveAudio: true,
      iceRestart: !!iceRestart,
    });
    if (oturum !== buOturum || kayit.pc.signalingState !== "stable") return;
    await kayit.pc.setLocalDescription(offer);
    await iceToplanmasiniBekle(kayit.pc);
    if (oturum !== buOturum || !kayit.pc.localDescription) return;
    await guvenilirSinyalGonder(buOturum.callId, {
      to: String(uid),
      kind: "offer",
      data: {
        type: kayit.pc.localDescription.type,
        sdp: kayit.pc.localDescription.sdp,
      },
    });
    if (kayit.baglantiTimer) clearTimeout(kayit.baglantiTimer);
    kayit.baglantiTimer = setTimeout(() => {
      kayit.baglantiTimer = null;
      if (oturum === buOturum && kayit.pc.connectionState !== "connected") {
        iceYenidenBaslat(String(uid));
      }
    }, 15000);
  } finally {
    kayit.makingOffer = false;
  }
}

/** İlk SDP'nin kendi adaylarını da taşımasını sağlar. Trickle ICE devam eder;
    bu bekleme yalnızca soğuk başlangıçta SSE yeniden bağlanırken aday kaybına
    karşı 2.5 saniyelik üst sınırlı bir güvenlik katmanıdır. */
function iceToplanmasiniBekle(pc, timeoutMs = 2500) {
  if (!pc || pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    let bitti = false;
    const tamamla = () => {
      if (bitti) return;
      bitti = true;
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", dinle);
      resolve();
    };
    const dinle = () => {
      if (pc.iceGatheringState === "complete") tamamla();
    };
    const timer = setTimeout(tamamla, timeoutMs);
    pc.addEventListener("icegatheringstatechange", dinle);
  });
}

/**
 * Bağlantıyı yeniden kurmaya çalışır (ağ değişimi, kopma).
 * En fazla 3 deneme yapar; her denemede `negotiationneeded` tetiklenir ve
 * teklif sahibi taraf yeni teklif gönderir.
 */
function iceYenidenBaslat(uid) {
  if (!oturum) return;
  const kayit = oturum.peers.get(String(uid));
  if (!kayit || !kayit.pc) return;

  if (kayit.toparlanmaDenemesi >= 3) return; // Sürekli denemeyle pil tüketme
  kayit.toparlanmaDenemesi += 1;

  if (kayit.tekliSahibi) {
    teklifOlusturVeGonder(String(uid), kayit, true).catch(() => {});
    return;
  }

  // Teklif sahibi olmayan taraf kendi başına restart offer üretmez; glare
  // yaratmadan sahibinden yeniden teklif ister.
  guvenilirSinyalGonder(oturum.callId, {
    to: String(uid),
    kind: "restart",
    data: { reason: "ice-failed" },
  }).catch(() => {});
}

/**
 * Yeni katılan kişiye TEKLİF gönderir.
 * Yalnızca aramada zaten bulunan taraf çağırır (glare önleme kuralı).
 * Bu tarafı "teklif sahibi" olarak işaretler; sonraki yeniden pazarlıkları
 * da hep bu taraf başlatır.
 */
export async function tekliGonder(uid) {
  if (!oturum) return;
  const kayit = esOlustur(String(uid));
  if (!kayit) return;
  kayit.tekliSahibi = teklifSahibiMi(uid);

  // Diğer taraf bu eş çiftinin kararlı teklif sahibidir. Peer kaydını önceden
  // oluşturmak ICE/offer erken gelirse kuyruğun hazır olmasını sağlar.
  if (!kayit.tekliSahibi) return;
  if (kayit.pc.connectionState === "connected") return;
  try {
    await teklifOlusturVeGonder(String(uid), kayit, false);
  } catch (_) {
    // Teklif üretilemedi — bağlantı kurulamayacak, UI zaman aşımıyla halleder.
  }
}

/** Ready yanıtındaki mevcut hazır katılımcılarla bağlantıyı garantiye alır.
    Bu yalnızca aramaya bağlanma anında çalışır; polling veya sürekli istek
    üretmez. Participant-joined SSE olayı kaçarsa SDP yine başlasın diye
    yedek güvenlik katmanıdır. */
export async function hazirEslerleBaglan(participants = []) {
  if (!oturum) return;
  const liste = Array.isArray(participants) ? participants : [];
  for (const p of liste) {
    const uid = String(p?.uid || "");
    if (!uid || uid === String(oturum.benimUid)) continue;
    if (p?.state !== "joined") continue;
    await tekliGonder(uid);
  }
}

/** Karşıdan gelen sinyali işler. */
export async function sinyalIsle({ from, kind, data, signalId }) {
  if (!oturum) return;
  const uid = String(from);
  if (signalId) {
    const key = String(signalId);
    if (oturum.islenenSinyaller.has(key)) return;
    oturum.islenenSinyaller.add(key);
    if (oturum.islenenSinyaller.size > 500) {
      oturum.islenenSinyaller.delete(oturum.islenenSinyaller.values().next().value);
    }
  }

  if (kind === "restart") {
    const kayit = oturum.peers.get(uid) || esOlustur(uid);
    if (!kayit) return;
    kayit.tekliSahibi = teklifSahibiMi(uid);
    if (kayit.tekliSahibi) {
      await teklifOlusturVeGonder(uid, kayit, true).catch(() => {});
    }
    return;
  }

  if (kind === "offer") {
    const kayit = esOlustur(uid);
    if (!kayit) return;
    try {
      const teklifCakismasi = kayit.makingOffer || kayit.pc.signalingState !== "stable";
      kayit.ignoreOffer = !kayit.polite && teklifCakismasi;
      if (kayit.ignoreOffer) return;
      if (teklifCakismasi && kayit.polite) {
        await kayit.pc.setLocalDescription({ type: "rollback" });
      }
      await kayit.pc.setRemoteDescription(new RTCSessionDescription(data));
      kayit.ignoreOffer = false;
      kayit.uzakAyarlandi = true;
      await bekleyenIceBosalt(kayit);

      const answer = await kayit.pc.createAnswer();
      await kayit.pc.setLocalDescription(answer);
      await iceToplanmasiniBekle(kayit.pc);
      if (!oturum || !kayit.pc.localDescription) return;
      await guvenilirSinyalGonder(oturum.callId, {
        to: uid,
        kind: "answer",
        data: {
          type: kayit.pc.localDescription.type,
          sdp: kayit.pc.localDescription.sdp,
        },
      });
    } catch (error) {
      console.warn("[AileCall:webrtc] Offer işlenemedi", error?.name || "offer_failed");
    }
    return;
  }

  if (kind === "answer") {
    const kayit = oturum.peers.get(uid);
    if (!kayit) return;
    try {
      if (kayit.pc.signalingState !== "have-local-offer") return;
      await kayit.pc.setRemoteDescription(new RTCSessionDescription(data));
      kayit.uzakAyarlandi = true;
      await bekleyenIceBosalt(kayit);
    } catch (error) {
      console.warn("[AileCall:webrtc] Answer işlenemedi", error?.name || "answer_failed");
    }
    return;
  }

  if (kind === "ice") {
    const kayit = oturum.peers.get(uid) || esOlustur(uid);
    if (!kayit) return;
    if (kayit.ignoreOffer) return;
    /* ICE adayı, uzak açıklama (SDP) ayarlanmadan ÖNCE gelebilir — bu normaldir
       ve trickle ICE'ın doğasıdır. Erken gelen adayları kuyruğa alıp SDP
       ayarlandıktan sonra ekliyoruz; aksi halde bağlantı sessizce kurulmaz. */
    if (!kayit.uzakAyarlandi) {
      if (kayit.bekleyenIce.length < 60) kayit.bekleyenIce.push(data);
      return;
    }
    try {
      await kayit.pc.addIceCandidate(new RTCIceCandidate(data));
    } catch (_) { /* geçersiz aday — yoksay */ }
  }
}

async function bekleyenIceBosalt(kayit) {
  const kuyruk = kayit.bekleyenIce.splice(0);
  for (const c of kuyruk) {
    try {
      await kayit.pc.addIceCandidate(new RTCIceCandidate(c));
    } catch (_) { /* yoksay */ }
  }
}

/** Bir katılımcı ayrıldığında onun bağlantısını kapat. */
export function esKapat(uid) {
  if (!oturum) return;
  const kayit = oturum.peers.get(String(uid));
  if (!kayit) return;
  if (kayit.toparlanmaTimer) clearTimeout(kayit.toparlanmaTimer);
  if (kayit.baglantiTimer) clearTimeout(kayit.baglantiTimer);
  try { kayit.pc.close(); } catch (_) { /* yoksay */ }
  try {
    kayit.audioEl.srcObject = null;
    kayit.audioEl.remove();
    oynatmaBekleyenleri.delete(kayit.audioEl);
  } catch (_) { /* yoksay */ }
  oturum.peers.delete(String(uid));
  durumBildir();
}

/* ══════════════════════════════════════════════════════════════════════════
   SES KONTROLÜ
   ══════════════════════════════════════════════════════════════════════════ */

/** Mikrofonu aç/kapat. Bağlantı kopmaz, yalnızca gönderim durur. */
export function susturmaDegistir() {
  if (!oturum || !oturum.yerelAkis) return false;
  oturum.susturuldu = !oturum.susturuldu;
  try {
    oturum.yerelAkis.getAudioTracks().forEach((t) => { t.enabled = !oturum.susturuldu; });
  } catch (_) { /* yoksay */ }
  durumBildir();
  return oturum.susturuldu;
}

export function susturulduMu() {
  return !!(oturum && oturum.susturuldu);
}

export default {
  oturumBaslat,
  oturumKapat,
  oturumVarMi,
  aktifCallId,
  tekliGonder,
  sinyalIsle,
  esKapat,
  susturmaDegistir,
  susturulduMu,
  durumDinleyiciAta,
  sesCikisiniCanlandir,
  hazirEslerleBaglan,
};
